import { inject } from '@angular/core';
import type { ResolveFn } from '@angular/router';
import { LocalStorageService } from '@capturum/ui/api';
import { DEFAULT_LANGUAGE } from '@core/configs/token.config';
import { InterpolatableTranslationObject, TranslateService } from '@ngx-translate/core';
import { PrimeNG } from 'primeng/config';

export const translationResolver: ResolveFn<InterpolatableTranslationObject> = (route, state) => {
  let navigatorLanguage = navigator.language.split('-')[0];
  const translateService = inject(TranslateService);
  const localStorageService = inject(LocalStorageService);
  const config = inject(PrimeNG);
  const defaultLanguage = inject(DEFAULT_LANGUAGE);
  const currentLanguage = localStorageService.getItem<string>('locale');

  navigatorLanguage = ['en', 'nl'].includes(navigatorLanguage) ? navigatorLanguage : defaultLanguage;

  translateService.onLangChange.subscribe(({ translations }) => {
    setPrimengLocale(translations as Dictionary<() => string>, config);
  });

  translateService.addLangs(['en', 'nl']);
  translateService.setDefaultLang(defaultLanguage);

  if (!currentLanguage) {
    localStorageService.setItem('locale', navigatorLanguage);
  }

  return translateService.use(currentLanguage || navigatorLanguage);
};

function setPrimengLocale(translations: Dictionary<() => string>, config: PrimeNG): void {
  config.setTranslation({
    dayNames: [
      translations['calendar-locale.day-names.sunday'](),
      translations['calendar-locale.day-names.monday'](),
      translations['calendar-locale.day-names.tuesday'](),
      translations['calendar-locale.day-names.wednesday'](),
      translations['calendar-locale.day-names.thursday'](),
      translations['calendar-locale.day-names.friday'](),
      translations['calendar-locale.day-names.saturday'](),
    ],
    dayNamesShort: [
      translations['calendar-locale.day-names.sun'](),
      translations['calendar-locale.day-names.mon'](),
      translations['calendar-locale.day-names.tue'](),
      translations['calendar-locale.day-names.wed'](),
      translations['calendar-locale.day-names.thu'](),
      translations['calendar-locale.day-names.fri'](),
      translations['calendar-locale.day-names.sat'](),
    ],
    dayNamesMin: [
      translations['calendar-locale.day-names.su'](),
      translations['calendar-locale.day-names.mo'](),
      translations['calendar-locale.day-names.tu'](),
      translations['calendar-locale.day-names.we'](),
      translations['calendar-locale.day-names.th'](),
      translations['calendar-locale.day-names.fr'](),
      translations['calendar-locale.day-names.sa'](),
    ],
    monthNames: [
      translations['calendar-locale.month-names.january'](),
      translations['calendar-locale.month-names.february'](),
      translations['calendar-locale.month-names.march'](),
      translations['calendar-locale.month-names.april'](),
      translations['calendar-locale.month-names.may'](),
      translations['calendar-locale.month-names.june'](),
      translations['calendar-locale.month-names.july'](),
      translations['calendar-locale.month-names.august'](),
      translations['calendar-locale.month-names.september'](),
      translations['calendar-locale.month-names.october'](),
      translations['calendar-locale.month-names.november'](),
      translations['calendar-locale.month-names.december'](),
    ],
    monthNamesShort: [
      translations['calendar-locale.month-names.jan'](),
      translations['calendar-locale.month-names.feb'](),
      translations['calendar-locale.month-names.mar'](),
      translations['calendar-locale.month-names.apr'](),
      translations['calendar-locale.month-names.may'](),
      translations['calendar-locale.month-names.jun'](),
      translations['calendar-locale.month-names.jul'](),
      translations['calendar-locale.month-names.aug'](),
      translations['calendar-locale.month-names.sep'](),
      translations['calendar-locale.month-names.oct'](),
      translations['calendar-locale.month-names.nov'](),
      translations['calendar-locale.month-names.dec'](),
    ],
    today: translations['calendar-locale.today'](),
    clear: translations['calendar-locale.clear'](),
    weekHeader: translations['calendar-locale.week-header'](),
  });
}
